import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import IntegrationRuntimeItem from "./IntegrationRuntimeItem.jsx";
import Badge from "../Badge.jsx";
import {
    formatRuntimeDate,
    getRuntimeErrorMessage,
    hasGoogleAdsPartialAccountAttention,
    sortIntegrationsByRuntime,
} from "./runtimeStatusUtils.js";
import {useIsAdministrator} from "../auth/rolesAndPermissions.js";
import {findBrand, useBrands} from "../../utils/brands/branding.js";

const RECENTLY_UPDATED_BADGE_WINDOW_MS = 20 * 60 * 1000;

function RuntimeInfoLine({label, value, danger = false}) {
    if (!value) {
        return null;
    }

    return (
        <div className={danger ? "text-datixo-negative mt-0" : 'mt-0'}>
            <span className={"font-bold text-slate-700 dark:text-slate-300"}>{label}: </span>
            <span>{value}</span>
        </div>
    );
}

export function shouldShowUpdatedBadge(lastPublishedAt, now = Date.now()) {
    if (!lastPublishedAt) {
        return false;
    }

    const publishedAtMs = new Date(lastPublishedAt).getTime();

    if (Number.isNaN(publishedAtMs)) {
        return false;
    }

    const ageMs = now - publishedAtMs;

    return ageMs >= 0 && ageMs <= RECENTLY_UPDATED_BADGE_WINDOW_MS;
}

function ProjectRuntimeBadge({projectRuntime, integrations, t}) {
    if (
        projectRuntime.status === "failed"
        || (integrations ?? []).some((integration) => integration.status === "failed")
    ) {
        return (
            <Badge color={"rose"}>
                <span>{t("runtime.project.failed", {defaultValue: "Chyba"})}</span>
            </Badge>
        );
    }

    if (projectRuntime.status === "running") {
        const label = projectRuntime.phase === "fetching"
            ? t("runtime.project.phase.fetching", {defaultValue: "Stahuji data"})
            : t("runtime.project.phase.recalculating", {defaultValue: "Zpracovávám data"});

        return (
            <Badge color={"yellow"} className={"gap-1.5"}>
                <FontAwesomeIcon icon={faRefresh} className={"animate-spin"} />
                <span>{label}</span>
            </Badge>
        );
    }

    if ((integrations ?? []).some(hasGoogleAdsPartialAccountAttention)) {
        const hasSuccessfulPartialAccount = (integrations ?? []).some((integration) =>
            hasGoogleAdsPartialAccountAttention(integration) && (integration.successfulAccounts ?? []).length > 0
        );

        return (
            <Badge color={"yellow"}>
                <span>
                    {hasSuccessfulPartialAccount
                        ? t("runtime.project.partially_updated", {defaultValue: "Částečně aktualizováno"})
                        : t("runtime.project.completed_with_warning", {defaultValue: "Dokončeno s upozorněním"})}
                </span>
            </Badge>
        );
    }

    if (!shouldShowUpdatedBadge(projectRuntime.lastPublishedAt)) {
        return null;
    }

    return (
        <Badge color={"emerald"}>
            <span>{t("runtime.project.updated", {defaultValue: "Aktualizováno"})}</span>
        </Badge>
    );
}

export default function ProjectRuntimeOverviewCard({
    projectData = null,
    projectRuntime,
    integrations,
    compact = false,
    showProjectIdentity = true,
}) {
    const {t} = useTranslation();
    const isAdministrator = useIsAdministrator();
    const brands = useBrands();
    const lastPublishedAt = formatRuntimeDate(projectRuntime.lastPublishedAt);
    const nextScheduledRunAt = formatRuntimeDate(projectRuntime.nextScheduledRunAt);
    const sortedIntegrations = sortIntegrationsByRuntime(
        integrations,
        (integration) => {
            const brand = findBrand(brands, integration.integrationName);

            return brand?.isERP === true || brand?.isLead === true;
        },
    );
    const projectError = projectRuntime.errorCode ? getRuntimeErrorMessage(t, projectRuntime.errorCode) : null;

    return (
        <div className={"app-surface rounded-xl dark:bg-slate-800/90 " + (compact ? "w-full" : "")}>
            {showProjectIdentity && projectData && (
                <div className={"flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700"}>
                    <img
                        alt={projectData.name}
                        src={projectData.logoUrl}
                        className={"h-10 w-10 rounded-lg border border-slate-200 object-cover dark:border-slate-600"}
                    />
                    <div className={"min-w-0"}>
                        <div className={"truncate font-semibold text-slate-900 dark:text-slate-100"}>
                            {projectData.name}
                        </div>
                        <div className={"text-xs text-slate-500 dark:text-slate-400"}>
                            {t("runtime.project.card_subtitle", {defaultValue: "Stav dat a zdrojů"})}
                        </div>
                    </div>
                </div>
            )}

            <div className={compact ? "p-3" : "p-4"}>
                <div className={"app-surface rounded-lg border bg-slate-50/90 dark:bg-slate-900/40 " + (compact ? "px-3 py-3" : "px-4 py-4")}>
                    <div className={"min-w-0"}>
                        <div className={"flex flex-wrap items-center gap-2"}>
                            <div className={"font-semibold text-black dark:text-slate-100 mr-auto"}>
                                {showProjectIdentity
                                    ? t("runtime.project.title", {defaultValue: "Aktuálnost dat"})
                                    : t("project.pre-calculation-status", {defaultValue: "Aktuálnost dat"})}
                            </div>
                            <ProjectRuntimeBadge
                                projectRuntime={projectRuntime}
                                integrations={integrations}
                                t={t}
                            />
                        </div>

                        <div className={"mt-2 text-xs text-slate-600 dark:text-slate-300"}>
                            <RuntimeInfoLine
                                label={t("runtime.project.last_update", {defaultValue: "Aktualizováno"})}
                                value={lastPublishedAt}
                            />
                            <RuntimeInfoLine
                                label={t("runtime.project.next_update", {defaultValue: "Další aktualizace"})}
                                value={nextScheduledRunAt}
                            />
                            <RuntimeInfoLine
                                label={t("runtime.project.error", {defaultValue: "Chyba"})}
                                value={projectError}
                                danger={true}
                            />
                            {isAdministrator && projectRuntime.error && (
                                <details className={"app-surface mt-2 rounded-md border border-violet-200 bg-violet-50/70 px-2.5 py-2 text-slate-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-slate-200"}>
                                    <summary className={"cursor-pointer font-semibold text-violet-700 dark:text-violet-300"}>
                                        {projectRuntime.errorMapped
                                            ? t("runtime.integration.technical_error_mapped", {defaultValue: "Technický detail · rozpoznaná chyba"})
                                            : t("runtime.integration.technical_error_unmapped", {defaultValue: "Technický detail · chybí mapování"})}
                                    </summary>
                                    <div className={"mt-2 break-words font-mono text-[11px] leading-relaxed"}>{projectRuntime.error}</div>
                                </details>
                            )}
                            <div className={"mt-2"}>
                                {t("runtime.project.description", {defaultValue: "Po stažení dat ze všech integrací proběhne jejich zpracování. Až poté se nové hodnoty zobrazí v přehledech."})}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={"mt-3"}>
                    <div className={"px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500"}>
                        {t("runtime.integrations.title", {defaultValue: "Zdroje dat"})}
                    </div>
                    <div className={"mt-2 space-y-2"}>
                        {sortedIntegrations.length === 0 && (
                            <div className={"app-surface rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400"}>
                                {t("runtime.integrations.empty", {defaultValue: "K projektu zatím není připojen žádný zdroj dat."})}
                            </div>
                        )}
                        {sortedIntegrations.map((integration) => (
                            <IntegrationRuntimeItem
                                key={integration.integrationId}
                                integration={integration}
                                compact={compact}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
